import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import type { Dict } from "./i18n/types";
import { en } from "./i18n/en";
import { de } from "./i18n/de";
import { zh } from "./i18n/zh";
import { ja } from "./i18n/ja";
import { ko } from "./i18n/ko";
import { ru } from "./i18n/ru";
import { pt } from "./i18n/pt";

const STORAGE_KEY = "sfn.lang";
const COOKIE_KEY = "sfn_lang";
const URL_PARAM = "lang";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

export const SUPPORTED = ["en", "de", "zh", "ja", "ko", "ru", "pt"] as const;
export type Lang = (typeof SUPPORTED)[number];
export type { Dict };

export const LANG_LABELS: Record<Lang, string> = {
  en: "English",
  de: "Deutsch",
  zh: "中文",
  ja: "日本語",
  ko: "한국어",
  ru: "Русский",
  pt: "Português",
};

/** Locale tag used for og:locale meta. */
const OG_LOCALE: Record<Lang, string> = {
  en: "en_US",
  de: "de_DE",
  zh: "zh_CN",
  ja: "ja_JP",
  ko: "ko_KR",
  ru: "ru_RU",
  pt: "pt_BR",
};

function isLang(v: string | null | undefined): v is Lang {
  return !!v && (SUPPORTED as readonly string[]).includes(v);
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  const secure =
    typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
}

function syncUrlParam(lang: Lang) {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(URL_PARAM) === lang) return;
    url.searchParams.set(URL_PARAM, lang);
    window.history.replaceState(window.history.state, "", url.toString());
  } catch {
    /* ignore */
  }
}

function detectInitialLang(): Lang {
  if (typeof window === "undefined") return "en";
  try {
    // 1. URL param wins — enables shareable localized links
    const urlLang = new URL(window.location.href).searchParams
      .get(URL_PARAM)
      ?.toLowerCase();
    if (isLang(urlLang)) return urlLang;
    // 2. localStorage
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (isLang(saved)) return saved;
    // 3. cookie (survives storage clears, cross-tab durable)
    const cookie = readCookie(COOKIE_KEY)?.toLowerCase();
    if (isLang(cookie)) return cookie;
    // 4. browser default
    const nav = window.navigator?.language?.slice(0, 2).toLowerCase();
    if (isLang(nav)) return nav;
  } catch {
    /* ignore */
  }
  return "en";
}

/**
 * Required keys — enforced by scripts/check-i18n.ts and the vitest parity
 * suite. Every locale must resolve each key to a non-empty, non-placeholder
 * string. Overlay locales inherit from EN via spread, so parity is automatic;
 * the guard still verifies every entry is present.
 */
export const REQUIRED_I18N_KEYS: readonly string[] = [
  "nav.ecosystem","nav.tokens","nav.protection","nav.secure","nav.faq","nav.connectWallet","nav.language",
  "hero.eyebrow","hero.title","hero.sub","hero.cta","hero.ghost","hero.stat1","hero.stat2","hero.stat3",
  "bento.title","bento.sub","bento.card1.title","bento.card1.text","bento.card2.title","bento.card2.text","bento.card3.title","bento.card3.text",
  "pilot.eyebrow","pilot.title","pilot.body","pilot.benchmark.label","pilot.benchmark.value","pilot.benchmark.desc","pilot.phase1","pilot.phase2","pilot.phase.arrow",
  "pilot.stat1.label","pilot.stat1.value","pilot.stat2.label","pilot.stat2.value","pilot.stat3.label","pilot.stat3.value",
  "about.eyebrow","about.titleA","about.titleB","about.body",
  "contact.eyebrow","contact.titleA","contact.titleB","contact.body","contact.name","contact.namePh","contact.email","contact.emailPh",
  "contact.subject","contact.subjectPh","contact.message","contact.messagePh","contact.sent","contact.successBody","contact.errorTitle","contact.errorBody",
  "contact.dismissOk","contact.dismissError","contact.responseNote","contact.send",
  "dual.eyebrow","dual.title","dual.sub","dual.meal4.t","dual.meal4.d","dual.meal2.t","dual.meal2.d",
  "protection.eyebrow","protection.title","protection.sub","protection.f1.title","protection.f1.text","protection.f2.title","protection.f2.text","protection.f3.title","protection.f3.text",
  "swap.eyebrow","swap.title","swap.sub","swap.connectWallet","swap.meal4.milestoneLabel","swap.meal4.milestoneProgress","swap.meal4.percent","swap.meal4.treasuryLabel","swap.meal4.treasuryNote","swap.meal4.tooltip","swap.meal4.tooltipAria","swap.meal2.rateLabel","swap.phantomApproving",
  "trust.eyebrow","trust.title","trust.body",
  "faq.eyebrow","faq.title","faq.sub","faq.q1","faq.a1","faq.q2","faq.a2","faq.q3","faq.a3","faq.q4","faq.a4",
  "roadmap.eyebrow","roadmap.title","roadmap.sub","roadmap.card1.title","roadmap.card1.desc","roadmap.card2.title","roadmap.card2.desc","roadmap.card3.title","roadmap.card3.desc","roadmap.card4.title","roadmap.card4.desc","roadmap.card5.title","roadmap.card5.desc","roadmap.card6.title","roadmap.card6.desc",
  "docs.eyebrow","docs.title","docs.sub","docs.btn1","docs.btn2","docs.btn3","docs.btn4",
  "verify.meal4.title","verify.meal4.sub","verify.meal4.cta","verify.meal2.title","verify.meal2.sub","verify.meal2.cta",
  "verify.item.meal4Supply.label","verify.item.meal4Supply.statusPassed","verify.item.meal4Supply.statusLive",
  "verify.item.meal4Mint.label","verify.item.meal4Mint.statusPassed","verify.item.meal4Mint.statusActive",
  "verify.item.meal4Freeze.label","verify.item.meal4Freeze.statusPassed","verify.item.meal4Freeze.statusActive",
  "verify.item.meal2Freeze.label","verify.item.meal2Freeze.statusPassed","verify.item.meal2Freeze.statusActive",
  "verify.item.meal2Mint.label","verify.item.meal2Mint.statusRevoked","verify.item.meal2Mint.statusActive",
  "verify.item.meal2Supply.label","verify.item.meal2Supply.statusLive",
  "verify.badge.connecting","verify.badge.cached","verify.badge.verified","verify.networkBusy",
  "transparency.jsonImmutable","transparency.jsonCeiling",
  "connection.eyebrow","connection.step1.title","connection.step1.text","connection.step2.title","connection.step2.text","connection.step3.title","connection.step3.text",
  "barometer.eyebrow","barometer.title.a","barometer.title.b","barometer.sub","barometer.rice","barometer.oil","barometer.solar",
  "footer.tag","footer.disclaimer","footer.about","footer.contact","footer.legal","footer.rights",
  "milestones.eyebrow","milestones.p1.title","milestones.p1.desc","milestones.p1.target","milestones.p2.title","milestones.p2.desc","milestones.p2.target",
  // VillagePando additions (Phase A)
  "vp.swap.pegged.badge","vp.swap.pegged.tooltip",
  "vp.swap.tax.eyebrow","vp.swap.tax.title","vp.swap.tax.intro","vp.swap.tax.total","vp.swap.tax.perTx",
  "vp.swap.tax.hardware.label","vp.swap.tax.hardware.detail",
  "vp.swap.tax.reforestation.label","vp.swap.tax.reforestation.detail",
  "vp.swap.tax.ops.label","vp.swap.tax.ops.detail",
  "treasury.eyebrow","treasury.title","treasury.desc","treasury.label","treasury.network","treasury.copy","treasury.copied","treasury.explorer",
  "treasury.status.checking","treasury.status.verified","treasury.status.degraded","treasury.status.invalid",
  "receipt.eyebrow","receipt.title","receipt.desc","receipt.amount","receipt.received","receipt.destination","receipt.network","receipt.hash","receipt.viewExplorer","receipt.close",
  "receipt.copyHash","receipt.hashCopied","receipt.download",
  "rpc.checking","rpc.degraded","rpc.invalid",
  "consent.title","consent.desc","consent.accept","consent.decline",
  "meal2.tab.web3",

  "dexRoadmap.title","dexRoadmap.phase1","dexRoadmap.phase2","dexRoadmap.phase3","dexRoadmap.active",
  "dexRoadmap.status.active","dexRoadmap.status.next","dexRoadmap.status.planned",
  "dexRoadmap.statusLive","dexRoadmap.statusFallback","dexRoadmap.visit","swap.quote.refreshing",

  "vp.nexus.trio.eyebrow","vp.nexus.trio.caption","vp.nexus.trio.step1","vp.nexus.trio.step2","vp.nexus.trio.step3","vp.nexus.trio.step4",
] as const;

/** Forbidden placeholder substrings (case-insensitive). */
export const I18N_PLACEHOLDER_PATTERNS: readonly RegExp[] = [
  /\bTODO\b/i,
  /\bTBD\b/i,
  /\bFIXME\b/i,
  /\bXXX\b/i,
  /lorem ipsum/i,
  /\[placeholder\]/i,
  /\{\{.*\}\}/,
];

export const translations: Record<Lang, Dict> = { en, de, zh, ja, ko, ru, pt };

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (k: string) => string;
}

const Ctx = createContext<I18nCtx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>("en");

  // Hydrate from URL / storage / cookie on mount, then mirror into all sinks.
  useEffect(() => {
    const initial = detectInitialLang();
    setLangState(initial);
    try {
      window.localStorage.setItem(STORAGE_KEY, initial);
      writeCookie(COOKIE_KEY, initial);
      syncUrlParam(initial);
    } catch {
      /* ignore */
    }
  }, []);

  // Cross-tab sync: react to storage events from other tabs.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && isLang(e.newValue)) setLangState(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
    let el = document.head.querySelector<HTMLMetaElement>('meta[property="og:locale"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("property", "og:locale");
      document.head.appendChild(el);
    }
    el.setAttribute("content", OG_LOCALE[lang] ?? OG_LOCALE.en);
  }, [lang]);

  const setLang = (l: Lang) => {
    // Guard: unsupported / removed locales (e.g. legacy "th") are ignored so
    // the dictionary lookup below can never hit an undefined entry.
    if (!isLang(l)) return;
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
      writeCookie(COOKIE_KEY, l);
      syncUrlParam(l);
    } catch {
      /* ignore */
    }
  };

  const t = (k: string) => translations[lang]?.[k] ?? translations.en[k] ?? k;

  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

/**
 * Inert English context used when a subtree renders outside I18nProvider.
 * Throwing here used to take down whole routes during SSR (/vision), so the
 * hook degrades to English instead of crashing the render.
 */
const FALLBACK_CTX: I18nCtx = {
  lang: "en",
  setLang: () => {},
  t: (k: string) => translations.en[k] ?? k,
};

export function useI18n() {
  const c = useContext(Ctx);
  if (!c) {
    if (typeof console !== "undefined") {
      console.warn("useI18n used outside I18nProvider — falling back to English.");
    }
    return FALLBACK_CTX;
  }
  return c;
}

